import {
  Briefcase,
  Mail,
  Eye,
  Send,
  Upload,
  GitBranch,
  LogOut,
  FileDown,
  ShieldCheck,
} from "lucide-react"
import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { useSelector } from "react-redux"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import {
  EmployeeDto,
  toEmployeeStatusLabel,
} from "@/modules/onboarding/employee/dtos"
import { PushToSageDialog } from "@/modules/onboarding/ui/components/PushToSageDialog"
import { PushDocumentsToSageDialog } from "@/modules/sage300/ui/PushDocumentsToSageDialog"
import { StartOffboardingDialog } from "@/modules/offboarding/ui/components/StartOffboardingDialog"
import { ExportToSageVipDialog } from "@/modules/onboarding/ui/components/ExportToSageVipDialog"
import { sage300ConnectorService } from "@/modules/profile/infrastructure/sage300ConnectorService"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import type { RootState } from "@/store"
import { AccessPanel } from "@/modules/resource-access/ui/components/AccessPanel"
import { useCanManageAccess } from "@/modules/resource-access/hooks/useCanManageAccess"
import { useCanEdit } from "@/modules/resource-access/hooks/useMyResourceAccess"

function getInitials(firstName: string, lastName: string) {
  return `${firstName.trim().slice(0, 1)}${lastName.trim().slice(0, 1)}`.toUpperCase()
}

function getOnboardingStatusBadge(status?: string) {
  if (!status) return null

  const config: Record<string, { label: string; className: string }> = {
    pending: {
      label: "Pending",
      className: "bg-muted text-foreground border-border",
    },
    started: {
      label: "Started",
      className: "bg-blue-100 text-blue-700 border-blue-200",
    },
    submitted: {
      label: "Submitted",
      className: "bg-yellow-100 text-yellow-800 border-yellow-200",
    },
    approved: {
      label: "Approved",
      className: "bg-emerald-100 text-emerald-700 border-emerald-200",
    },
    rejected: {
      label: "Rejected",
      className: "bg-red-100 text-red-700 border-red-200",
    },
    completed: {
      label: "Completed",
      className: "bg-emerald-100 text-emerald-700 border-emerald-200",
    },
    offboarding: {
      label: "Offboarding",
      className: "bg-orange-100 text-orange-700 border-orange-200",
    },
    terminated: {
      label: "Terminated",
      className: "bg-red-100 text-red-700 border-red-200",
    },
  }

  const match = config[status.toLowerCase()]
  return match ? (
    <Badge className={match.className}>{match.label}</Badge>
  ) : (
    <Badge className="bg-muted text-foreground border-border">{status}</Badge>
  )
}

const COOLDOWN_SECONDS = 60

export function EmployeeCard({
  employee,
  onView,
  onInvite,
  isInviting,
  onOffboardingStarted,
  hasSageVip = false,
  hasSage300 = false,
}: {
  employee: EmployeeDto
  onView: (employee: EmployeeDto) => void
  onInvite?: (employee: EmployeeDto) => void
  isInviting?: boolean
  onOffboardingStarted?: () => void
  hasSageVip?: boolean
  hasSage300?: boolean
}) {
  const organizationId = useSelector((s: RootState) =>
    selectCurrentOrganizationId(s),
  )
  const navigate = useNavigate()
  const [cooldown, setCooldown] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [isSageOpen, setIsSageOpen] = useState(false)
  const [isDocPushOpen, setIsDocPushOpen] = useState(false)
  const [isOffboardingOpen, setIsOffboardingOpen] = useState(false)
  const [isVipOpen, setIsVipOpen] = useState(false)
  const [isAccessPanelOpen, setIsAccessPanelOpen] = useState(false)
  const canManageAccess = useCanManageAccess()
  const { canEdit, isRestricted, level } = useCanEdit("Employee", employee.id)
  const [hasSageSubmission, setHasSageSubmission] = useState(false)

  const isCoolingDown = cooldown > 0
  const onboardingStatus = employee.onboardingStatus?.toLowerCase()
  const statusLabel = toEmployeeStatusLabel(employee.status)
  const isOffboarding =
    onboardingStatus === "offboarding" || statusLabel === "Offboarding"
  const isTerminated =
    onboardingStatus === "terminated" || statusLabel === "Terminated"
  const isCompleted =
    onboardingStatus === "completed" || onboardingStatus === "approved"
  const isActive = statusLabel === "Active"
  const canOffboard = isActive && !isOffboarding && !isTerminated
  const hasAcceptedInvite =
    onboardingStatus !== "pending" ||
    ((statusLabel as string) !== "Pending" && statusLabel !== "Invited")

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!organizationId || (!isActive && !isCompleted)) {
      setHasSageSubmission(false)
      return
    }

    let cancelled = false
    sage300ConnectorService
      .getSubmission(organizationId, employee.id)
      .then((submission) => {
        if (cancelled) return
        const isRealSubmission =
          !!submission?.entityCode && submission.status !== "FailedEmployeePush"
        setHasSageSubmission(isRealSubmission)
      })
      .catch(() => {
        if (!cancelled) setHasSageSubmission(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationId, employee.id, employee.status, isActive, isCompleted])

  const handleInvite = async () => {
    if (!onInvite) return
    await onInvite(employee)
    setCooldown(COOLDOWN_SECONDS)
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!)
          timerRef.current = null
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  return (
    <Card className="bg-card border border-border hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-11 h-11 bg-muted rounded-full flex items-center justify-center shrink-0">
              <span className="text-sm font-semibold text-foreground">
                {getInitials(employee.firstName, employee.lastName)}
              </span>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-semibold text-lg text-foreground truncate">
                  {employee.firstName} {employee.lastName}
                </h3>
                {getOnboardingStatusBadge(employee.onboardingStatus)}
                {isRestricted && (
                  <Badge variant="outline" className="text-muted-foreground">
                    {level === "Read" ? "Read-only" : "Restricted"} access
                  </Badge>
                )}
                {(isActive || isCompleted) && hasSageSubmission && (
                  <Badge className="bg-green-100 text-green-700 border-green-200">
                    Sage Submitted
                  </Badge>
                )}
                {employee.hasActiveWorkflow && (
                  <Badge className="bg-blue-100 text-blue-700 border-blue-200 flex items-center gap-1">
                    <GitBranch className="w-3 h-3" />
                    Workflow In Progress
                  </Badge>
                )}
                {employee.recruitmentApplicationId != null && (
                  <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                    From Recruitment
                  </Badge>
                )}
              </div>

              <div className="mt-1 flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                <div className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{employee.email}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  <span>{employee.jobTitle}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onView(employee)}
              className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
            >
              <Eye className="w-4 h-4 mr-2" />
              View Details
            </Button>

            {onInvite && !hasAcceptedInvite && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleInvite}
                disabled={isInviting || isCoolingDown || !canEdit}
                title={!canEdit ? "You have read-only access to this employee" : undefined}
                className="text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300"
              >
                <Send className="w-4 h-4 mr-2" />
                {isInviting
                  ? "Sending..."
                  : isCoolingDown
                    ? `Resend Invite (${cooldown}s)`
                    : "Resend Invite"}
              </Button>
            )}

            {isCompleted && !hasSageVip && (
              <Button
                size="sm"
                variant={hasSageSubmission ? "outline" : "default"}
                onClick={() => {
                  if (!hasSage300 && !hasSageSubmission) {
                    toast.error("Connect Sage 300 first to push employees.", {
                      action: {
                        label: "Connect",
                        onClick: () => navigate("/integrations"),
                      },
                    })
                    return
                  }
                  setIsSageOpen(true)
                }}
                disabled={!hasSageSubmission && !canEdit}
                title={
                  !hasSageSubmission && !canEdit
                    ? "You have read-only access to this employee"
                    : undefined
                }
                className={
                  hasSageSubmission
                    ? "text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300"
                    : "bg-green-600 hover:bg-green-700 text-white border-0"
                }
              >
                {hasSageSubmission ? (
                  <>
                    <Eye className="w-4 h-4 mr-2" />
                    View Submission
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Push to Sage
                  </>
                )}
              </Button>
            )}

            {isCompleted && hasSageVip && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsVipOpen(true)}
                className="text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Export to VIP
              </Button>
            )}

            {isOffboarding && (
              <Button
                size="sm"
                variant="outline"
                disabled
                className="text-orange-600 border-orange-200 opacity-70 cursor-not-allowed"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Offboarding Started
              </Button>
            )}

            {isTerminated && (
              <Button
                size="sm"
                variant="outline"
                disabled
                className="text-red-600 border-red-200 opacity-70 cursor-not-allowed"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Terminated
              </Button>
            )}

            {canOffboard && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsOffboardingOpen(true)}
                disabled={!canEdit}
                title={!canEdit ? "You have read-only access to this employee" : undefined}
                className="text-orange-600 border-orange-200 hover:bg-orange-50 hover:border-orange-300"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Start Offboarding
              </Button>
            )}

            {canManageAccess && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAccessPanelOpen(true)}
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                Manage Access
              </Button>
            )}
          </div>
        </div>
      </CardContent>
      <PushToSageDialog
        open={isSageOpen}
        onOpenChange={setIsSageOpen}
        employee={employee}
        onSubmissionSuccess={() => {
          setHasSageSubmission(true)
          // Pushing documents to Sage is disabled — auto-trigger removed and
          // the "Push Documents" button in PushToSageDialog is commented out.
          // setIsDocPushOpen(true)
        }}
        onPushDocuments={() => setIsDocPushOpen(true)}
      />
      <PushDocumentsToSageDialog
        open={isDocPushOpen}
        onOpenChange={setIsDocPushOpen}
        organizationId={organizationId ?? ""}
        employeeId={employee.id}
        employeeName={`${employee.firstName} ${employee.lastName}`}
      />
      <ExportToSageVipDialog
        open={isVipOpen}
        onOpenChange={setIsVipOpen}
        employees={[employee]}
      />
      <StartOffboardingDialog
        open={isOffboardingOpen}
        onOpenChange={setIsOffboardingOpen}
        employee={employee}
        onSuccess={onOffboardingStarted}
      />
      <AccessPanel
        open={isAccessPanelOpen}
        onClose={() => setIsAccessPanelOpen(false)}
        type="Employee"
        resourceId={employee.id}
        resourceName={`${employee.firstName} ${employee.lastName}`}
      />
    </Card>
  )
}
